// Sincronización masiva con Discogs (Req. 10.2, 10.3, 10.3.a, 10.3.b, 10.12,
// 10.12.a, 10.12.b, 10.13).
//
// Flujo:
//   1. Filtro avanzado (reutiliza AdvancedSearchForm) para acotar el subconjunto.
//   2. Lanzar el job (self-invoke async en el backend); polling del progreso.
//   3. Control real: pausar / reanudar / cancelar (estado 'cancelled' visible).
//   4. Histórico de jobs anteriores (api.sync.list).
//   5. Detalle por disco (api.sync.items) agrupado por resultado
//      (actualizado / revisión / omitido / error) con puntaje, motivo, campos
//      que cambiaron y enlace a la ficha.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import type { SearchCriteria } from '../api/endpoints.js';
import { AlertMessage, Card, Collapsible, Icon, PageHeader, Spinner } from '../ui/components.js';
import type { SyncItemOutcome, SyncJob, SyncJobItem } from '../types.js';
import { AdvancedSearchForm } from './AdvancedSearchForm.js';
import { fieldLabel } from './syncFieldLabels.js';

/** ¿El job sigue vivo (conviene seguir haciendo polling)? */
function isActive(status: SyncJob['status']): boolean {
  return status === 'pending' || status === 'running' || status === 'paused';
}

const STATUS_TONE: Record<SyncJob['status'], string> = {
  pending: 'bg-surface-rack text-on-surface-variant border-border-subtle',
  running: 'bg-primary-container/15 text-primary border-primary-container/40',
  paused: 'bg-goldmine-vg/15 text-goldmine-vg border-goldmine-vg/40',
  completed: 'bg-goldmine-mint/15 text-goldmine-mint border-goldmine-mint/40',
  cancelled: 'bg-surface-rack text-on-surface-variant border-border-subtle',
  error: 'bg-error-container/20 text-error border-error/40',
};

export function BulkSyncView() {
  const { t } = useTranslation();
  const api = useApi();
  const [criteria, setCriteria] = useState<SearchCriteria>({});
  const [autoApply, setAutoApply] = useState(false);
  const [job, setJob] = useState<SyncJob | null>(null);
  const [history, setHistory] = useState<SyncJob[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Vista previa del subconjunto: nº de discos que afectaría el filtro actual.
  // `null` = aún no se ha previsualizado. Se invalida al cambiar los criterios.
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Carga una página del histórico desde el servidor. El backend ya devuelve
  // solo trabajos masivos (`scope='bulk'`) paginados, así que la paginación es
  // real contra la BD y no se pierden trabajos antiguos.
  const loadHistory = useCallback(
    async (page = 0) => {
      try {
        const res = await api.sync.list({
          limit: HISTORY_PAGE_SIZE,
          offset: page * HISTORY_PAGE_SIZE,
        });
        setHistory(res.items);
        setHistoryTotal(res.total ?? res.items.length);
        setHistoryPage(page);
      } catch {
        /* el histórico es secundario; no bloquea el lanzamiento */
      }
    },
    [api],
  );

  useEffect(() => {
    void loadHistory(0);
    return () => stopPolling();
  }, [loadHistory, stopPolling]);

  // Aplica los filtros y cuenta cuántos discos se verían afectados, SIN lanzar la
  // sincronización (Req. 10.2: previsualizar el subconjunto antes de ejecutar).
  async function preview() {
    setPreviewing(true);
    setError(null);
    try {
      const res = await api.records.advancedSearch(criteria, { page: 1, page_size: 1 });
      setPreviewCount(res.total);
    } catch (err) {
      setPreviewCount(null);
      setError(err instanceof Error ? err.message : t('bulkSync.errFilters'));
    } finally {
      setPreviewing(false);
    }
  }

  function updateCriteria(next: SearchCriteria) {
    setCriteria(next);
    // El conteo previo deja de ser válido al cambiar los filtros.
    setPreviewCount(null);
  }

  const startPolling = useCallback(
    (id: string) => {
      stopPolling();
      pollRef.current = window.setInterval(async () => {
        try {
          const j = await api.sync.getJob(id);
          setJob(j);
          if (!isActive(j.status)) {
            stopPolling();
            void loadHistory(0);
          }
        } catch {
          /* reintenta en el próximo tick */
        }
      }, 2000);
    },
    [api, loadHistory, stopPolling],
  );

  async function launch() {
    setBusy(true);
    setError(null);
    try {
      const created = await api.sync.createBulk({ filter: criteria, auto_apply: autoApply });
      setJob(created);
      if (isActive(created.status)) startPolling(created.id);
      void loadHistory(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bulkSync.errLaunch'));
    } finally {
      setBusy(false);
    }
  }

  async function control(action: 'pause' | 'resume' | 'cancel') {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.sync[action](job.id);
      setJob(updated);
      if (action === 'resume' && isActive(updated.status)) startPolling(job.id);
      if (action === 'cancel') stopPolling();
      void loadHistory(historyPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bulkSync.errControl'));
    } finally {
      setBusy(false);
    }
  }

  function openJob(j: SyncJob) {
    setJob(j);
    if (isActive(j.status)) startPolling(j.id);
    else stopPolling();
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('bulkSync.pageTitle')}
        subtitle={t('bulkSync.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
      <AlertMessage message={error} />

      <Card title={t('bulkSync.newBatchTitle')} icon="sync">
        <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
          {t('bulkSync.newBatchHint')}
        </p>
        <AdvancedSearchForm
          criteria={criteria}
          onChange={updateCriteria}
          onSearch={() => void preview()}
          onClear={() => {
            setCriteria({});
            setPreviewCount(null);
          }}
          title={t('bulkSync.filtersTitle')}
          icon="filter_list"
        />

        {/* Vista previa del subconjunto: "Buscar" solo aplica los filtros y
            muestra cuántos discos se verían afectados; no lanza nada. */}
        {previewing && (
          <p className="mt-space-sm font-body-sm text-body-sm text-on-surface-variant">
            {t('bulkSync.applyingFilters')}
          </p>
        )}
        {!previewing && previewCount !== null && (
          <div className="mt-space-sm p-space-sm rounded-lg bg-primary-container/10 border border-primary-container/40 flex items-center gap-space-xs">
            <Icon name="filter_list" className="text-[18px] text-primary" />
            <span className="font-body-sm text-body-sm text-on-surface">
              {previewCount === 0
                ? t('bulkSync.noneMatch')
                : t('bulkSync.willSync', { count: previewCount })}
            </span>
          </div>
        )}

        <label className="flex items-center gap-space-xs mt-space-sm font-body-sm text-body-sm text-on-surface cursor-pointer">
          <input
            type="checkbox"
            className="accent-[#38bdf8] w-4 h-4"
            checked={autoApply}
            onChange={(e) => setAutoApply(e.target.checked)}
          />
          {t('bulkSync.autoApply')}
        </label>
        <button
          className="btn-primary w-full mt-space-md"
          type="button"
          disabled={busy || previewCount === 0}
          onClick={() => void launch()}
        >
          <Icon name="cloud_sync" className="text-[18px]" />
          {busy ? t('bulkSync.launching') : t('bulkSync.launch')}
        </button>
      </Card>

      {/* Historial primero (desplegable, 10 por página): es la navegación más
          lógica — se elige un trabajo y debajo aparece su detalle. */}
      <HistoryPanel
        history={history}
        total={historyTotal}
        page={historyPage}
        currentId={job?.id ?? null}
        onOpen={openJob}
        onPageChange={(p) => void loadHistory(p)}
      />

      {/* Detalle del trabajo activo o seleccionado. */}
      {job && (
        <JobPanel
          job={job}
          busy={busy}
          onControl={(a) => void control(a)}
        />
      )}
      </div>
    </div>
  );
}

/** Panel del job activo/seleccionado: progreso, contadores, controles y detalle. */
function JobPanel({
  job,
  busy,
  onControl,
}: {
  job: SyncJob;
  busy: boolean;
  onControl: (action: 'pause' | 'resume' | 'cancel') => void;
}) {
  const { t } = useTranslation();
  // Contadores tolerantes: una respuesta parcial (o un job recién cancelado) no
  // debe romper el render por acceder a campos ausentes.
  const total = job.total ?? 0;
  const processed = job.processed ?? 0;
  const progress = total > 0 ? Math.round((processed / total) * 100) : 0;

  return (
    <Card
      title={t('bulkSync.jobTitle', { id: job.id.slice(0, 8) })}
      icon="conveyor_belt"
      action={
        <span className={`badge-goldmine border ${STATUS_TONE[job.status] ?? STATUS_TONE.pending}`}>
          {t(`bulkSync.status.${job.status}`, { defaultValue: job.status })}
        </span>
      }
    >
      <div className="meter-track mb-space-xs">
        <div className="h-full bg-primary-container transition-all" style={{ width: `${progress}%` }} />
      </div>
      <div className="flex items-center justify-between">
        <span className="font-code-catalog text-body-sm text-primary">
          {progress}% · {processed}/{total}
        </span>
        <span className="font-code-catalog text-body-sm text-on-surface-variant flex items-center gap-1">
          <Icon name="timer" className="text-[16px]" />
          {isActive(job.status) ? (
            <LiveElapsed startedAt={parseTs(job.created_at)} running={job.status === 'running'} />
          ) : (
            (jobDuration(job) ?? '—')
          )}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-space-xs mt-space-sm">
        <Stat label={t('bulkSync.statUpdated')} value={job.updated ?? 0} tone="text-goldmine-mint" />
        <Stat label={t('bulkSync.statReview')} value={job.flagged_for_review ?? 0} tone="text-goldmine-vg" />
        <Stat label={t('bulkSync.statSkipped')} value={job.skipped ?? 0} />
        <Stat label={t('bulkSync.statProcessed')} value={`${processed}/${total}`} />
      </div>

      <AlertMessage message={job.error} className="mt-space-sm" />

      <div className="flex items-center gap-space-sm mt-space-md">
        {job.status === 'running' && (
          <button className="btn-secondary flex-1" type="button" disabled={busy} onClick={() => onControl('pause')}>
            <Icon name="pause" className="text-[18px]" /> {t('bulkSync.pause')}
          </button>
        )}
        {job.status === 'paused' && (
          <button className="btn-primary flex-1" type="button" disabled={busy} onClick={() => onControl('resume')}>
            <Icon name="play_arrow" className="text-[18px]" /> {t('bulkSync.resume')}
          </button>
        )}
        {isActive(job.status) && (
          <button className="btn-ghost !text-priority-urgent" type="button" disabled={busy} onClick={() => onControl('cancel')}>
            <Icon name="stop" className="text-[18px]" /> {t('bulkSync.cancel')}
          </button>
        )}
      </div>

      {!isActive(job.status) && <JobItems jobId={job.id} status={job.status} />}
    </Card>
  );
}

const OUTCOME_META: Record<SyncItemOutcome, { icon: string; tone: string }> = {
  updated: { icon: 'check_circle', tone: 'text-goldmine-mint' },
  flagged: { icon: 'flag', tone: 'text-goldmine-vg' },
  skipped: { icon: 'do_not_disturb_on', tone: 'text-on-surface-variant' },
  error: { icon: 'error', tone: 'text-error' },
};

const OUTCOME_ORDER: SyncItemOutcome[] = ['updated', 'flagged', 'skipped', 'error'];

/** Detalle por disco de un job terminado, agrupado por resultado. */
function JobItems({ jobId, status }: { jobId: string; status: SyncJob['status'] }) {
  const { t } = useTranslation();
  const api = useApi();
  const [items, setItems] = useState<SyncJobItem[] | null>(null);
  const [counts, setCounts] = useState<Record<SyncItemOutcome, number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<Set<SyncItemOutcome>>(new Set(['updated', 'flagged']));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    api.sync
      .items(jobId)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setCounts(res.counts);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, jobId]);

  if (loading) return <div className="mt-space-md"><Spinner label={t('bulkSync.loadingResults')} /></div>;

  if (failed) {
    return (
      <p className="mt-space-md font-body-sm text-body-sm text-on-surface-variant">
        {t('bulkSync.errLoadItems')}
      </p>
    );
  }

  const summaryTone =
    status === 'completed' ? 'text-goldmine-mint' : status === 'cancelled' ? 'text-on-surface-variant' : 'text-error';

  const grouped = (outcome: SyncItemOutcome) => (items ?? []).filter((it) => it.outcome === outcome);

  const total = items?.length ?? 0;

  return (
    <div className="mt-space-md flex flex-col gap-space-sm">
      <p className={`font-body-sm text-body-sm ${summaryTone}`}>
        {status === 'completed' && t('bulkSync.finished')}
        {status === 'cancelled' && t('bulkSync.cancelledMsg')}
        {status === 'error' && t('bulkSync.errorMsg')}
        {' '}
        {t('bulkSync.processedCount', { count: total })}
      </p>

      {total === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {t('bulkSync.noItemResults')}
        </p>
      ) : (
        OUTCOME_ORDER.map((outcome) => {
          const list = grouped(outcome);
          const n = counts?.[outcome] ?? list.length;
          if (n === 0) return null;
          return (
            <Collapsible
              key={outcome}
              title={t('bulkSync.groupTitle', { label: t(`bulkSync.outcome.${outcome}`), count: n })}
              open={open.has(outcome)}
              onToggle={() =>
                setOpen((prev) => {
                  const next = new Set(prev);
                  if (next.has(outcome)) next.delete(outcome);
                  else next.add(outcome);
                  return next;
                })
              }
            >
              <div className="flex flex-col gap-space-xs">
                {list.map((it) => (
                  <ItemRow key={it.id} item={it} />
                ))}
              </div>
            </Collapsible>
          );
        })
      )}
    </div>
  );
}

/** Fila de un disco: resultado, puntaje, motivo, campos cambiados y enlace. */
function ItemRow({ item }: { item: SyncJobItem }) {
  const { t } = useTranslation();
  const meta = OUTCOME_META[item.outcome] ?? OUTCOME_META.skipped;
  // Defensa: si el backend devolviera changed_fields como algo que no es array,
  // no debe reventar el render (Array.isArray lo blinda).
  const changes = Array.isArray(item.changed_fields) ? item.changed_fields : [];
  // Datos básicos del álbum para el resumen (artista — título, nº interno, cat nr).
  const title = item.record_title ?? t('bulkSync.defaultTitle');
  const artist = item.artist_name ?? null;
  const catNr = item.label_catalog_nr ?? null;
  const internal = item.internal_cat_num ?? null;

  return (
    <div className="surface-rack-card !p-space-sm flex flex-col gap-space-xs">
      <div className="flex items-start gap-space-sm">
        <Icon name={meta.icon} className={`text-[18px] shrink-0 mt-0.5 ${meta.tone}`} />
        <div className="flex-1 min-w-0 flex flex-col">
          <span className="font-body-sm text-body-sm text-on-surface truncate">
            {artist ? `${artist} — ${title}` : title}
            {internal != null && <span className="text-on-surface-variant"> · #{internal}</span>}
          </span>
          {catNr && (
            <span className="font-code-catalog text-body-sm text-on-surface-variant truncate">
              {t('bulkSync.catPrefix', { catalog: catNr })}
            </span>
          )}
          <Link to={`/records/${item.record_id}`} className="font-body-sm text-body-sm text-primary underline w-fit">
            {t('bulkSync.viewRecord')}
          </Link>
        </div>
        {item.score != null && (
          <span className="font-code-catalog text-body-sm text-on-surface-variant shrink-0">
            {Math.round(item.score)}%
          </span>
        )}
        {item.discogs_release_id != null && (
          <a
            href={`https://www.discogs.com/release/${item.discogs_release_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-on-surface-variant hover:text-primary shrink-0"
            aria-label={t('bulkSync.viewOnDiscogs')}
          >
            <Icon name="open_in_new" className="text-[16px]" />
          </a>
        )}
      </div>

      {item.reason && (
        <p className="font-body-sm text-body-sm text-on-surface-variant">{item.reason}</p>
      )}

      {changes.length > 0 && (
        <ul className="flex flex-col gap-1 mt-1">
          {changes.map((c, i) => (
            <li key={`${c.field}-${i}`} className="font-body-sm text-body-sm">
              <span className="section-label text-on-surface-variant">{fieldLabel(t, c.field)}: </span>
              <span className="text-on-surface-variant line-through">{fmt(c.current)}</span>
              <Icon name="arrow_forward" className="text-[14px] mx-1 text-on-surface-variant" />
              <span className="text-goldmine-mint">{fmt(c.proposed)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

/** Parsea una fecha ISO (o el formato de Postgres) a epoch ms; null si no es válida. */
function parseTs(raw: string | null | undefined): number | null {
  if (!raw) return null;
  // El backend puede devolver 'YYYY-MM-DD HH:MM:SS.ffffff' (sin zona, UTC).
  const iso = raw.includes('T') ? raw : raw.replace(' ', 'T') + (raw.endsWith('Z') ? '' : 'Z');
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/** Formatea una duración en ms como "1h 04m 09s" / "4m 09s" / "9s". */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** Duración total de un job terminado (updated_at − created_at). */
function jobDuration(job: SyncJob): string | null {
  const start = parseTs(job.created_at);
  const end = parseTs(job.updated_at);
  if (start === null || end === null) return null;
  return formatDuration(end - start);
}

/** Fecha/hora local en que se ejecutó el trabajo (a partir de created_at). */
function formatJobDate(raw: string | null | undefined): string {
  const ms = parseTs(raw);
  if (ms === null) return '—';
  return new Date(ms).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Tamaño de página del histórico de trabajos. */
const HISTORY_PAGE_SIZE = 10;

/**
 * Cronómetro en vivo del tiempo transcurrido desde `startedAt`. Refresca cada
 * segundo mientras `running` es true; al detenerse, congela el último valor.
 */
function LiveElapsed({ startedAt, running }: { startedAt: number | null; running: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);
  if (startedAt === null) return <>—</>;
  return <>{formatDuration(now - startedAt)}</>;
}

/**
 * Histórico de trabajos de sincronización masiva, paginado (10 por página)
 * CONTRA EL SERVIDOR: cada página es una consulta `GET /sync/jobs` con
 * `limit`/`offset`, de modo que se puede llegar a trabajos antiguos y no se
 * depende de una única respuesta inicial. El backend ya devuelve solo `bulk`.
 */
function HistoryPanel({
  history,
  total,
  page,
  currentId,
  onOpen,
  onPageChange,
}: {
  history: SyncJob[];
  total: number;
  page: number;
  currentId: string | null;
  onOpen: (job: SyncJob) => void;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  const pageItems = history;
  const pageCount = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE));

  return (
    <Card title={t('bulkSync.historyTitle')} icon="history" collapsible defaultOpen>
      {total === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {t('bulkSync.noHistory')}
        </p>
      ) : (
        <>
          <ul className="flex flex-col gap-space-xs">
            {pageItems.map((j) => (
              <li key={j.id}>
                <button
                  type="button"
                  onClick={() => onOpen(j)}
                  className={`w-full text-left surface-rack-card !p-space-sm flex items-center gap-space-sm ${
                    j.id === currentId ? 'border-primary-container/60' : ''
                  }`}
                >
                  <span className={`badge-goldmine border ${STATUS_TONE[j.status] ?? STATUS_TONE.pending}`}>
                    {t(`bulkSync.status.${j.status}`, { defaultValue: j.status })}
                  </span>
                  <span className="flex-1 min-w-0 flex flex-col">
                    <span className="font-code-catalog text-body-sm text-on-surface truncate">
                      {j.id.slice(0, 8)}
                    </span>
                    <span className="font-body-sm text-body-sm text-on-surface-variant flex items-center gap-1">
                      <Icon name="event" className="text-[14px]" />
                      {formatJobDate(j.created_at)}
                    </span>
                    <span className="font-code-catalog text-body-sm text-on-surface-variant flex items-center gap-1">
                      <Icon name="timer" className="text-[14px]" />
                      {jobDuration(j) ?? '—'}
                    </span>
                  </span>
                  <span className="font-body-sm text-body-sm text-on-surface-variant shrink-0">
                    {j.updated ?? 0}✓ · {j.flagged_for_review ?? 0}⚑ · {j.processed ?? 0}/{j.total ?? 0}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {pageCount > 1 && (
            <div className="flex items-center justify-between mt-space-sm">
              <button
                type="button"
                className="btn-ghost btn-sm"
                disabled={page === 0}
                onClick={() => onPageChange(Math.max(0, page - 1))}
              >
                <Icon name="chevron_left" className="text-[18px]" /> {t('bulkSync.previous')}
              </button>
              <span className="font-body-sm text-body-sm text-on-surface-variant">
                {t('bulkSync.pageOf', { page: page + 1, total: pageCount })}
              </span>
              <button
                type="button"
                className="btn-ghost btn-sm"
                disabled={page >= pageCount - 1}
                onClick={() => onPageChange(Math.min(pageCount - 1, page + 1))}
              >
                {t('bulkSync.next')} <Icon name="chevron_right" className="text-[18px]" />
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function Stat({ label, value, tone = 'text-on-surface' }: { label: string; value: string | number; tone?: string }) {
  return (
    <div className="surface-rack-card !p-space-sm flex flex-col">
      <span className="section-label text-on-surface-variant">{label}</span>
      <span className={`font-code-catalog text-body-md font-bold ${tone}`}>{value}</span>
    </div>
  );
}
